"use client";

import type React from "react";

import { useIntersectionObserver } from "@/hooks/use-intersection-observer";
import { cn } from "@/lib/utils";
import type { ReactNode } from "react";

type AnimationDirection =
  | "up"
  | "down"
  | "left"
  | "right"
  | "fade"
  | "scale"
  | "none";

interface ScrollRevealProps {
  children: ReactNode;
  className?: string;
  direction?: AnimationDirection;
  delay?: number;
  duration?: number;
  threshold?: number;
  rootMargin?: string;
  once?: boolean;
}

export function ScrollReveal({
  children,
  className,
  direction = "up",
  delay = 0,
  duration = 0.5,
  threshold = 0.1,
  rootMargin = "0px",
  once = true,
}: ScrollRevealProps) {
  const { ref, isIntersecting } = useIntersectionObserver({
    threshold,
    rootMargin,
    freezeOnceVisible: once,
  });

  const getAnimationClass = () => {
    if (!isIntersecting) {
      switch (direction) {
        case "up":
          return "translate-y-10 opacity-0";
        case "down":
          return "translate-y-[-10px] opacity-0";
        case "left":
          return "translate-x-10 opacity-0";
        case "right":
          return "translate-x-[-10px] opacity-0";
        case "scale":
          return "scale-95 opacity-0";
        case "fade":
          return "opacity-0";
        case "none":
          return "";
        default:
          return "translate-y-10 opacity-0";
      }
    }
    return "";
  };

  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      className={cn(
        className,
        getAnimationClass(),
        "transition-all will-change-transform",
        isIntersecting ? "transform-none opacity-100" : ""
      )}
      style={{
        transitionDuration: `${duration}s`,
        transitionDelay: `${delay}s`,
      }}
    >
      {children}
    </div>
  );
}
